import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import BackButton from '../components/BackButton';
import SEOHead from '../components/SEOHead';

const SurgicalDivision: React.FC = () => {
  const navigate = useNavigate();

  const handleBack = () => {
    navigate('/');
  };

  return (
    <>
      <SEOHead
        title="Surgical Division | Medical Officer - Plastic & Reconstructive Surgery"
        description="Dr. Muhamad Bashirushdan Azemi's medical practice specializing in Plastic & Reconstructive Surgery. View case reports, research publications, and clinical expertise."
        keywords="plastic surgery, reconstructive surgery, medical officer, malaysia, kelantan, microsurgery, facial reconstruction, surgical procedures"
        url="https://bash.my/surgical"
        type="website"
      />
      <div className="min-h-screen bg-black text-white font-rajdhani relative overflow-hidden">
      {/* Background effects */}
      <div className="fixed inset-0 z-[1] opacity-30">
        <div className="absolute inset-0 bg-gradient-to-br from-red-900/20 to-black"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 pt-24 pb-16 px-4">
        <BackButton onClick={handleBack} />
        
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <div className="text-red-400 font-mono text-lg mb-4 cyber-glow">
            bash@neural-core:~$ cat /medical_division/overview.md
          </div>
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6">
            SURGICAL DIVISION
          </h1>
          <p className="text-gray-400 text-xl max-w-4xl mx-auto">
            Medical Officer specializing in Plastic & Reconstructive Surgery
          </p>
        </motion.div>

        <div className="max-w-7xl mx-auto">
          {/* Medical Overview */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-gradient-to-br from-red-900/20 to-red-700/10 border border-red-400/30 p-8 rounded-lg mb-8"
          >
            <h2 className="text-red-400 font-mono text-2xl mb-6">MEDICAL_PROFILE</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-white font-semibold mb-3">Current Position</h3>
                <p className="text-gray-300 mb-4">
                  Medical Officer at Plastic & Reconstructive Surgery Unit, actively involved in 
                  surgical procedures, patient care, and medical research.
                </p>
                <h3 className="text-white font-semibold mb-3">Specialization</h3>
                <ul className="text-gray-300 space-y-2">
                  <li>• Plastic & Reconstructive Surgery</li>
                  <li>• Trauma Surgery</li>
                  <li>• Emergency Medicine</li>
                  <li>• Surgical Innovation</li>
                </ul>
              </div>
              <div>
                <h3 className="text-white font-semibold mb-3">Statistics</h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-400">Surgical Procedures:</span>
                    <span className="text-red-400 font-bold">156+</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Success Rate:</span>
                    <span className="text-green-400 font-bold">98.1%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Publications:</span>
                    <span className="text-blue-400 font-bold">8</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Clinical Experience:</span>
                    <span className="text-cyan-400 font-bold">4+ years</span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Case Reports & Publications */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Case Reports */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.5 }}
              className="bg-black/60 border border-red-400/30 p-6 rounded-lg"
            >
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-3 h-3 bg-red-400 rounded-full animate-pulse"></div>
                <h3 className="text-red-400 font-mono text-xl">CASE_REPORTS</h3>
              </div>
              
              <div className="space-y-4">
                {[
                  {
                    title: "Microsurgical Free Flap Reconstruction in Complex Facial Trauma",
                    status: "Published",
                    journal: "Malaysian Journal of Plastic Surgery",
                    year: "2024"
                  },
                  {
                    title: "Innovation in Digital Surgical Planning for Reconstructive Procedures",
                    status: "Under Review",
                    journal: "Asian Journal of Reconstructive Surgery",
                    year: "2024"
                  },
                  {
                    title: "Emergency Management of Severe Maxillofacial Injuries in Rural Settings",
                    status: "Published",
                    journal: "Emergency Medicine & Trauma Care",
                    year: "2023"
                  },
                  {
                    title: "Telemedicine Applications in Post-Operative Wound Care Monitoring",
                    status: "Published",
                    journal: "Digital Health Innovation",
                    year: "2023"
                  }
                ].map((report, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.7 + (i * 0.1) }}
                    className="border-l-2 border-red-400/50 pl-4 hover:border-red-400 transition-colors cursor-pointer"
                  >
                    <div className="text-white font-semibold">{report.title}</div>
                    <div className="text-gray-400 text-sm">{report.journal}, {report.year}</div>
                    <div className={`text-xs ${report.status === 'Published' ? 'text-green-400' : 'text-yellow-400'}`}>
                      {report.status}
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Research Studies */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.6 }}
              className="bg-black/60 border border-blue-400/30 p-6 rounded-lg"
            >
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-3 h-3 bg-blue-400 rounded-full animate-pulse"></div>
                <h3 className="text-blue-400 font-mono text-xl">RESEARCH_STUDIES</h3>
              </div>
              
              <div className="space-y-4">
                {[
                  {
                    title: "AI-Assisted 3D Surgical Planning in Reconstructive Surgery",
                    role: "Co-Principal Investigator",
                    status: "Ongoing",
                    completion: "75%"
                  },
                  {
                    title: "Machine Learning Applications in Post-Operative Outcome Prediction",
                    role: "Lead Researcher",
                    status: "Data Analysis",
                    completion: "60%"
                  },
                  {
                    title: "Telemedicine Integration in Rural Surgical Care Delivery",
                    role: "Contributing Author",
                    status: "Published",
                    completion: "100%"
                  },
                  {
                    title: "Digital Biomarkers for Wound Healing Assessment",
                    role: "Research Collaborator",
                    status: "Proposal Review",
                    completion: "25%"
                  }
                ].map((study, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.8 + (i * 0.1) }}
                    className="border-l-2 border-blue-400/50 pl-4 hover:border-blue-400 transition-colors cursor-pointer"
                  >
                    <div className="text-white font-semibold">{study.title}</div>
                    <div className="text-gray-400 text-sm">{study.role}</div>
                    <div className="flex justify-between items-center mt-2">
                      <span className="text-blue-400 text-xs">{study.status}</span>
                      <span className="text-cyan-400 text-xs">{study.completion}</span>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>

          {/* Medical Education & Certifications */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9 }}
            className="mt-8 bg-black/60 border border-green-400/30 p-6 rounded-lg"
          >
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
              <h3 className="text-green-400 font-mono text-xl">EDUCATION_&_CERTIFICATIONS</h3>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="text-white font-semibold mb-3">Medical Education</h4>
                <div className="space-y-2 text-gray-300">
                  <div>• Doctor of Medicine (M.D.)</div>
                  <div>• Plastic & Reconstructive Surgery Residency</div>
                  <div>• Advanced Trauma Life Support (ATLS)</div>
                  <div>• Microsurgery Certification</div>
                </div>
              </div>
              <div>
                <h4 className="text-white font-semibold mb-3">Professional Development</h4>
                <div className="space-y-2 text-gray-300">
                  <div>• International Surgery Conferences</div>
                  <div>• Medical Technology Innovation</div>
                  <div>• Research Methodology</div>
                  <div>• Medical Ethics & Law</div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
      </div>
    </>
  );
};

export default SurgicalDivision;